import { BitrixDisableUserGuard } from '@components/guards/bitrixUserGuard/BitrixUserGuard';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import useWidgetTabsContext from '@pages/AdditionalServices/containers/widgetTabsContext';
import React, { MouseEvent, ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DELIVERY_SECTION_ID } from './consts';
import { getTabSectionId } from './helpers';
import CalculationsMenuStyledContainer, { CalculationsMenuPopoverStyled } from './StyledComponents';
import { useCustomsPaymentsSectionIds } from './useCustomsPaymentsSectionIds';

/**
 * Пропсы компонента
 */
type CalculationsMenuProps = {
  buttonLabel: string;
  linkLabel: string;
  linkHref: string;
  children: (sectionId: string, isOpen: boolean) => ReactNode;
};

/**
 * Общее краткое меню расчетов для истории и избранного
 */
export const CalculationsMenu = ({
  buttonLabel,
  linkLabel,
  linkHref,
  children,
}: CalculationsMenuProps) => {
  const { t } = useTranslation(['TubsContainer', 'Actions']);
  const { tabs } = useWidgetTabsContext();
  const customsSectionIds = useCustomsPaymentsSectionIds(tabs.Tabs);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string>(() =>
    getTabSectionId(tabs.ActiveTabData)
  );

  // Popover сам не пересчитывает позицию при изменении содержимого, и меню уходит за край экрана.
  // Перерисовываем его в следующем кадре. MutationObserver нужен потому, что при смене раздела
  // с данными из кэша высота возвращается в том же кадре и ResizeObserver этого не видит.
  const [, setRepositionTick] = useState(0);
  const paperObserversCleanupRef = useRef<(() => void) | null>(null);

  const paperRefCallback = useCallback((paper: HTMLDivElement | null) => {
    paperObserversCleanupRef.current?.();
    paperObserversCleanupRef.current = null;

    if (!paper) {
      return;
    }

    let frameId = 0;
    const scheduleReposition = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => setRepositionTick((t) => t + 1));
    };

    const resizeObserver = new ResizeObserver(scheduleReposition);
    const mutationObserver = new MutationObserver(scheduleReposition);
    resizeObserver.observe(paper);
    mutationObserver.observe(paper, { childList: true, subtree: true });

    paperObserversCleanupRef.current = () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  const visibleTabs = tabs.Tabs.filter((tab) => !customsSectionIds.includes(getTabSectionId(tab)));

  const sectionId = visibleTabs.some((tab) => getTabSectionId(tab) === activeSectionId)
    ? activeSectionId
    : DELIVERY_SECTION_ID;

  const isOpen = !!anchorEl;

  if (customsSectionIds.includes(getTabSectionId(tabs.ActiveTabData))) {
    return null;
  }

  const toggleMenuHandler = (event: MouseEvent<HTMLElement>) => {
    if (isOpen) {
      setAnchorEl(null);
      return;
    }

    setActiveSectionId(getTabSectionId(tabs.ActiveTabData));
    setAnchorEl(event.currentTarget);
  };

  const closeMenuHandler = () => {
    setAnchorEl(null);
  };

  return (
    <CalculationsMenuStyledContainer>
      <BitrixDisableUserGuard>
        <Link
          component="button"
          type="button"
          underline="none"
          className="calculations-menu__trigger"
          onClick={toggleMenuHandler}
        >
          {buttonLabel}
        </Link>
      </BitrixDisableUserGuard>
      <CalculationsMenuPopoverStyled
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closeMenuHandler}
        slotProps={{ paper: { ref: paperRefCallback } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div className="calculations-menu__header">
          <Tabs
            className="calculations-menu__tabs"
            value={sectionId}
            onChange={(_, newSectionId: string) => setActiveSectionId(newSectionId)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {visibleTabs.map((tab) => {
              const tabSectionId = getTabSectionId(tab);

              return (
                <Tab
                  key={`calculations-menu-tab--${tabSectionId}`}
                  value={tabSectionId}
                  label={t(tab.label)}
                />
              );
            })}
          </Tabs>
          <IconButton
            size={'small'}
            className="calculations-menu__close"
            aria-label={t('Actions:Close')}
            onClick={closeMenuHandler}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className="calculations-menu__list">{children(sectionId, isOpen)}</div>
        <div className="calculations-menu__footer">
          <Link href={linkHref} underline="hover">
            {linkLabel}
          </Link>
        </div>
      </CalculationsMenuPopoverStyled>
    </CalculationsMenuStyledContainer>
  );
};
